import { useState } from 'react';
import { useQuery } from "@tanstack/react-query";
import { usePassport } from '../../../hooks/usePassport';
import TeacherCodeModal from './TeacherCodeModal';

const MissionDetailPage = ({ exploreCode, setExploreCode }) => {
	const API_URL = import.meta.env.VITE_API_URL;
	const { passport } = usePassport();
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [form, setForm] = useState({exploreCode: exploreCode, exploreLevel: passport.passportLevel, successType: "", successId: ""});

	const { data } = useQuery({
		queryKey: ['explore', exploreCode],
		queryFn: async () => {
			const response = await fetch(`${API_URL}/bbs/ajax.get_explore.php?exploreCode=${exploreCode}`);
			return response.json();
		}
	});
	const items = data?.data?.list ?? null;
	const missions = passport?.passportResult?.[exploreCode]?.levels?.[passport.passportLevel]?.missions || [];

	const countries = {
        IMG : {1:'🎨',2:'🖼️',3:'🎭'},
        VID : {1:'🎬',2:'🎥',3:'📹'},
        STY : {1:'📖',2:'📚',3:'📜'},
        MUS : {1:'🎵',2:'🎼',3:'🎹'},
        WRT : {1:'✍️',2:'🖊️',3:'✒️'},
        MTH : {1:'🔢',2:'➗',3:'∑'},
        WND : {1:'🔬',2:'🧪',3:'⚗️'},
    };
    const icon = countries[exploreCode]?.[passport.passportLevel] ?? '❓';

    const badges = [
        {id: 'speed', icon:'🏃', name:'스피드러너', desc:'한 달 안에 한 나라 완주'},
        {id: 'perfect', icon:'🎯', name:'완벽주의자', desc:'모든 프로젝트 100점'},
        {id: 'presenter', icon:'🎤', name:'발표왕', desc:'프로젝트 10번 발표'},
        {id: 'passion', icon:'🔥', name:'열정왕', desc:'3일 연속 미션 완수'}
    ];

    const handleClick = (type, id) => {
    	setForm(prev => ({...prev, successType: type, successId: id}));
    	setIsModalOpen(true);
    }

	return (
		<div className="bg-slate-100 p-2 min-h-screen flex justify-center items-start">
			<div className="relative w-full bg-white p-[30px] m-[20px] border-[3px] border-dashed border-slate-400 rounded-[15px] before:content-[''] before:absolute before:w-[30px] before:height-[30px] before:h-[30px] before:bg-slate-100 before:rounded-full before:border-[3px] before:border-slate-400 before:top-1/2 before:-translate-y-1/2 before:-left-[18px] after:content-[''] after:absolute after:w-[30px] after:height-[30px] after:h-[30px] after:bg-slate-100 after:rounded-full after:border-[3px] after:border-slate-400 after:top-1/2 after:-translate-y-1/2 after:-right-[18px]">
				<div className="flex justify-between items-center mb-5 pb-5 border-b-[3px] border-dashed border-slate-200">
					<div>
						<div className="text-[48px] font-[900] text-blue-800 leading-none">{icon}</div>
						<div className="text-[14px] text-slate-500 font-bold mt-2">{exploreCode}</div>
					</div>
					<div className="text-[36px] text-slate-400">✈️</div>
					<div className="text-right">
						<div className="text-[24px] font-bold text-blue-900">{items?.[0]?.exploreName}</div>
					</div>
				</div>
				<div className="mb-5">
					<div className="flex justify-between text-[12px] mb-2 font-bold">
						<span>미션 스탬프</span>
						<span className="text-red-500 font-bold">{passport?.passportResult?.[exploreCode]?.levels?.[passport.passportLevel]?.missions?.filter(m => m.done)?.length || 0} / {data?.data?.pagination?.total_count}</span>
					</div>
					<div className="grid grid-cols-5 gap-[10px] my-5">
						{items?.map((item, index) => {
							const doneCount = missions.filter(m => m.done).length;
							const isDone = index < doneCount;
							return (
								<div
									key={index}
									className={`h-24 rounded-lg flex items-center justify-center text-xl transition-all
								        ${isDone ? 'bg-gradient-to-br from-yellow-100 to-yellow-300 border-2 border-yellow-500 shadow-md' : 'bg-gray-50 border-2 border-dashed border-gray-200'}
						          	`}
          						>
			    			      	{isDone ? '⭐' : ''}
          						</div>
          					);
						})}
					</div>
				</div>
				<div className="max-h-[280px] overflow-y-auto mb-5 space-y-[10px]">
					{items?.map((item, index) => {
						const isDone = missions.some(m => m.id === item.exploreId && m.done);
						return (
							<div
								key={item.exploreId}
								className={`p-[10px] rounded-lg flex justify-between items-center shadow-sm transition-all
									${isDone ? 'bg-green-50 border-2 border-green-500' : 'bg-gray-50 border-2 border-gray-200'}
								`}
					        >
					        	<div className="flex-1">
					        		<div className="font-bold text-[13px] text-gray-800 text-left">
					        			{index + 1}. {item.exploreMission}
					        		</div>
					        	</div>
					        	<div className="ml-2">
					        		{isDone ? (
					        			<span className="text-green-500 text-[12px] font-bold">
					        				✓ 완료
					        			</span>
					        			) : (
					        			<button
					        				onClick={() => handleClick("mission", item.exploreId)}
					        				className="bg-blue-500 hover:bg-blue-600 text-white px-[14px] py-[6px] rounded-md text-[11px] font-bold transition-colors"
					        			>
					        				스탬프 받기
					        			</button>
					        			)}
					        	</div>
					        </div>
						);
					})}
				</div>
				<div className="p-5 bg-gradient-to-br from-amber-50 to-yellow-200 rounded-xl border-2 border-amber-500">
					<div className="font-bold mb-3 text-amber-900 text-[14px]">🏅 {items?.[0]?.exploreName} 특별 배지</div>
					<div className="grid grid-cols-2 gap-2">
						{badges?.map((item, index) => (
							<div
								key={item.id}
								onClick={() => handleClick("badge", item.id)}
								className="p-[10px] bg-white border-2 border-gray-300 rounded-lg cursor-pointer transition-all duration-300 text-center"
							>
								<div className="text-[28px] mb-[4px]">{item.icon}</div>
								<div className="text-[10px] font-bold text-gray-500">{item.name}</div>
							</div>
						))}
					</div>
					<div className="mt-3 text-center text-[11px] font-bold text-amber-900">
						획득한 배지: 0 / {badges.length}
					</div>
				</div>
			</div>
			{isModalOpen && (
				<TeacherCodeModal 
					form={form}
					onClose={() => setIsModalOpen(false)} 
				/>
			)}
		</div>
	);
}

export default MissionDetailPage;